import { useEffect, useState } from "react";
import { ArrowRight, FileDown, FileText, ListChecks, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

/**
 * Full-width Clarity Issue-Mapping Starter Kit band: the unified lead magnet
 * (regulatory issue-spotting checklist + complete sample memorandum in one email). Sits
 * about halfway down the landing page as a scroll-stop for new visitors:
 * authority (Briefing Desk) -> understanding (the Starter Kit) -> action (the
 * Assessment).
 */
export const ChecklistLeadBand = ({ onStartAssessment, source = "landing_band" }) => {
  const [challenge, setChallenge] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", website: "", marketing_consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const fetchChallenge = () =>
    api.get("/checklist/challenge").then((r) => setChallenge(r.data.challenge_token)).catch(() => {});

  useEffect(() => {
    fetchChallenge();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!challenge) {
      fetchChallenge();
      return toast.error("One moment, still preparing the form. Try again.");
    }
    setSending(true);
    try {
      await api.post("/checklist/lead", {
        email: form.email,
        full_name: form.name,
        organization: "Not provided",
        challenge_token: challenge,
        website: form.website,
        source,
        marketing_consent: !!form.marketing_consent,
      });
      setSent(true);
      track("checklist_lead_captured", { source });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
      setChallenge(null);
      fetchChallenge(); // nonce is one-shot; fetch a fresh one for retry
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="checklist-lead-band" className="border-t border-ink bg-blueprint">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 bg-ink text-white px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] mb-5">
            <FileDown size={12} strokeWidth={1.75} />
            Free resource &middot; One email, two documents
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold leading-[0.98] text-ink">
            The Clarity Issue-Mapping Starter Kit.
          </h2>
          <p className="mt-5 font-mono text-sm text-ink/85 max-w-xl leading-relaxed">
            Everything you need to see what CLARITY produces, written for the people accountable for digital asset risk.
          </p>
          <ul className="mt-8 space-y-5 max-w-xl">
            <li className="flex items-start gap-4">
              <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 border border-ink bg-white/70">
                <ListChecks size={18} strokeWidth={1.5} className="text-ink" />
              </span>
              <div>
                <div className="font-serif text-xl tracking-tight leading-tight">The Digital Assets Regulatory Issue-Spotting Checklist</div>
                <div className="font-mono text-xs text-ink/85 mt-1 leading-relaxed">Sections mapping the regulatory perimeter, with diligence questions per domain.</div>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 border border-ink bg-white/70">
                <FileText size={18} strokeWidth={1.5} className="text-ink" />
              </span>
              <div>
                <div className="font-serif text-xl tracking-tight leading-tight">A complete Sample Memorandum</div>
                <div className="font-mono text-xs text-ink/85 mt-1 leading-relaxed">The full output of the engine, so you see exactly what you get before you spend a dollar.</div>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-ink bg-white p-6 sm:p-8 shadow-[8px_8px_0px_0px_#0A2240]">
            {!sent ? (
              <form onSubmit={submit} data-testid="lead-band-form">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-blueprint mb-4">
                  <Mail size={13} strokeWidth={1.75} />
                  Sent instantly by email
                </div>
                <div className="space-y-3">
                  <input
                    data-testid="lead-band-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                    className="w-full border border-ink/40 bg-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-blueprint"
                  />
                  <input
                    data-testid="lead-band-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="Work email"
                    className="w-full border border-ink/40 bg-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-blueprint"
                  />
                  {/* Honeypot, hidden from humans, must remain blank */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                    aria-hidden="true"
                  />
                </div>
                <label className="mt-4 flex items-start gap-2 cursor-pointer">
                  <input
                    data-testid="lead-band-marketing-consent"
                    type="checkbox"
                    checked={form.marketing_consent}
                    onChange={(e) => setForm((f) => ({ ...f, marketing_consent: e.target.checked }))}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="font-mono text-[10px] leading-relaxed text-ink/65">
                    Optional: send me occasional educational emails and relevant offers. This is not required to get the Starter Kit, and you can unsubscribe at any time.
                  </span>
                </label>
                <button
                  data-testid="lead-band-submit"
                  type="submit"
                  disabled={sending}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-ink text-white py-4 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-blueprint hover:border-blueprint disabled:opacity-60 transition-colors"
                >
                  {sending ? "Sending…" : "Email me the kit"} <ArrowRight size={14} />
                </button>
                <p className="mt-3 font-mono text-[10px] text-ink/60 leading-relaxed">
                  The Starter Kit is sent to your email. Follow-up emails are only sent if you opt in above.
                </p>
              </form>
            ) : (
              <div data-testid="lead-band-sent">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#059669] mb-3">Sent</div>
                <div className="font-serif text-2xl tracking-tight font-semibold leading-snug">
                  Check your inbox. The Clarity Issue-Mapping Starter Kit is on the way.
                </div>
                <p className="mt-3 font-mono text-xs text-ink/75 leading-relaxed">
                  Ready for the next step? The CLARITY Assessment maps your own scenario to a recommended entry point in three minutes.
                </p>
                <button
                  type="button"
                  data-testid="lead-band-sent-cta"
                  onClick={() => { track("quiz_open", { source: "landing_band_sent" }); onStartAssessment?.(); }}
                  className="mt-4 inline-flex items-center gap-2 bg-ink text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border border-ink hover:bg-blueprint hover:border-blueprint transition-colors"
                >
                  Start Your CLARITY Assessment <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
